import { UIIcon } from '../config/icons';
import { ABFilterBar } from '../lib/ab/filter-bar';
import { ABObjectCount } from '../lib/ab/widgets-admin';
import { FPage, FPageSectionView, fPagesGetListView, f_pages } from '../lib/mdl/f-page';
import { FPageBlock, FPageBlockType } from '../lib/mdl/f-page-block';
import { f_routes } from '../lib/mdl/f-route';
import { canManageLockedPages, canManagePages } from '../lib/me/can';
import { UIButton, makeActionButton } from '../lib/ui/component/button';
import { uiInitCodeEditor } from '../lib/ui/component/codeeditor';
import { UIDialog } from '../lib/ui/component/dialog';
import { UII18NInput } from '../lib/ui/component/i18n-input';
import { UIImageInput } from '../lib/ui/component/image';
import { UITextInput } from '../lib/ui/component/input';
import { UILabeled } from '../lib/ui/component/labeled';
import { UITable, UITableDefinition } from '../lib/ui/component/table';
import { UITabs } from '../lib/ui/component/tabs';
import { uiToast } from '../lib/ui/component/toast';
import { UICheckbox, UIFlagsToggles, UISwitch } from '../lib/ui/component/toggle';
import { addTooltip } from '../lib/ui/component/tooltip';
import { showWarning } from '../lib/ui/component/warning';
import { $el } from '../lib/ui/elements';
import { $fmt } from '../lib/ui/fmt';
import { UIScreen } from '../lib/ui/screen';
import { UIRelPos, UIValue } from '../lib/ui/types';
import { UIView, UIViewSpec } from '../lib/ui/view';
import { YArrayDataSource } from '../lib/y/data-source';
import { $log, xavis_experiments } from '../lib/y/debug';
import { $form } from '../lib/y/form';
import { $i18n, YLanguage } from '../lib/y/i18n';
import { $location } from '../lib/y/location';
import { yCopy } from '../lib/y/object';
import { $time } from '../lib/y/time';
import { YId } from '../lib/y/types';
import { AdminPageEditor } from './page_editor';
import { AdminRouteScreen } from './routes';

const language_values: UIValue<YLanguage>[] = [{ key: 'en', name: 'EN' }, { key: 'sv', name: 'SV' }, { key: 'de', name: 'DE' }];

class PageInfoTab extends UIView {
    constructor(page: FPageSectionView | undefined) {
        super('div', 'tl-admin-page');

        const can_locked = canManageLockedPages();
        // const can_edit = canManageLockedPages() || !page || !page.is_locked;

        const is_locked = new UISwitch({ disabled: !can_locked });
        const is_hidden = new UISwitch;
        const flags = new UITextInput();
        const name = new UITextInput({ required: true });
        const resource_id = new UITextInput({ null_if_empty: true });
        const title = new UII18NInput(new UITextInput);
        const description = new UII18NInput(new UITextInput({ lines: 4 }));
        const og_title = new UII18NInput(new UITextInput);
        const og_description = new UII18NInput(new UITextInput({ lines: 4 }));
        const og_image = new UII18NInput(new UIImageInput({ scope: 'route', aspect: 1200 / 630 }));
        const languages = new UIFlagsToggles(language_values, v => new UICheckbox({ key: v.key, title: v.name }), { classes: 'l-toggle-row', required: true })

        const form = $form({ is_locked, is_hidden, name, resource_id, languages, metadata: $form({ title, description, og_title, og_description, og_image }) });

        const section = $el.FORM('ui-section w-form l-form', [
            new UILabeled(is_locked, { before: 'Locked' }),
            can_locked && new UILabeled(is_hidden, { before: 'Hidden' }),
            new UILabeled(name, { above: 'Name' }),
            new UILabeled(resource_id, { above: 'Resource Id' }),
            new UILabeled(languages, { above: 'Languages' }),
            xavis_experiments && new UILabeled(flags, { above: 'Flags' }),
            $el.H3('heading', 'Page metadata'),
            $el.P('input-hint', 'Will be used instead of the default one if provided'),
            new UILabeled(title, { above: 'Title' }),
            new UILabeled(description, { above: 'Description' }),
            new UILabeled(og_title, { above: 'OG title' }),
            new UILabeled(og_description, { above: 'OG description' }),
            new UILabeled(og_image, { above: 'OG image', below: 'Recommended: 1200x630 ~100kb', classes: 'r-desktop' })
        ]);

        if(page) {
            form.value = page;

            this.classList.add('m-sidebar');
            const sidebar = $el.DIV('tl-sidebar');

            if(page.flags)
                flags.value = Object.keys(page.flags).join(', ');

            sidebar.ui_add([
                $el.DIV('tl-sidebar-heading', 'Routes to this page')
            ]);

            f_routes.getAll().then(routes => {
                routes = routes.filter(r => r.target_type == 'page' && r.target_page_id == page.id);

                if(routes.length) {
                    for(const r of routes) {
                        sidebar.ui_add($el.B(r.path));
                    }
                } else {
                    sidebar.ui_add($el.B('No routes'));
                }

                sidebar.ui_add(new UIButton({
                    kind: 'k-text', title: 'New route', on_click: () => {
                        $location.push({ transient: new AdminRouteScreen(undefined, { page_id: page.id }) });
                    }
                }));
            });

            this.ui_add(sidebar);
        } else {
            languages.value = { en: true, sv: true, de: true };
        }

        const button = new UIButton({
            icon: UIIcon.THUMBS_UP, kind: 'k-square', classes: 'm-round ui-action-button', on_click: () => {
                if(!form.validate()) return;
                const value = form.value;
                if(xavis_experiments)
                    value.flags = Object.fromEntries(flags.value.split(',').map(s => [s.trim(), true]).filter(s => s[0]));

                return f_pages.upsert(page?.id, value).then($location.pop, uiToast);
            }
        });

        addTooltip(page ? 'Update' : 'Create', button, { offset: 8, hover: button, position: UIRelPos.Above });

        this.ui_add([section, button]);
    }
}

class PageContentTab extends UIView {
    constructor(page: FPageSectionView) {
        super('div', 'tl-admin-page m-sidebar m-left-sidebar m-fixed ui-tint-lime');

        const editor = new AdminPageEditor(page, 'r-main');
        const form = $form({ sections: editor.sections });

        form.value = page;

        const button = new UIButton({
            icon: UIIcon.THUMBS_UP, kind: 'k-square', classes: 'm-round ui-action-button', on_click: () => {
                if(!form.validate()) return;

                f_pages.update(page.id, form.value).then(() => {
                    uiToast('Page saved!');
                }, uiToast);
            }
        });

        addTooltip('Save', button, { offset: 8, hover: button, position: UIRelPos.Above });

        const can_save = canManageLockedPages() || !page.is_locked;

        this.ui_add([
            editor.toolbar,
            editor,
            $el.DIV('tl-sidebar m-editor l-v m-left', [
                editor.tree
            ]),
            $el.DIV('tl-sidebar m-editor l-v', [
                editor.properties_container,
                $el.S(),
                editor.fields
            ]),
            can_save && button
        ]);
    }
}

class PageTranslationTab extends UIView {
    constructor(page: FPageSectionView) {
        super('div', 'tl-admin-page m-fixed m-fixed-2 ui-tint-lime');

        const editor = new AdminPageEditor(page, 'r-main', true);
        const form = $form({ sections: editor.sections });

        form.value = page;

        const button = new UIButton({
            icon: UIIcon.THUMBS_UP, kind: 'k-square', classes: 'm-round ui-action-button', on_click: () => {
                if(!form.validate()) return;

                f_pages.update(page.id, form.value).then(() => {
                    uiToast('Page saved!');
                }, uiToast);
            }
        });

        addTooltip('Save', button, { offset: 8, hover: button, position: UIRelPos.Above });

        const can_save = canManageLockedPages() || !page.is_locked;

        this.ui_add([
            editor.toolbar,
            editor,
            can_save && button
        ]);
    }
}

class PageJSONTab extends UIView {
    constructor(page: FPageSectionView) {
        super('div', ['tl-admin-page', 'm-fixed', 'w-block']);

        const editor_element = $el.DIV(['r-main']);

        let editor: AceAjax.Editor | undefined;

        uiInitCodeEditor().then(() => {
            editor = ace.edit(editor_element);
            editor.setShowFoldWidgets(false);
            editor.setShowPrintMargin(false);
            editor.renderer.setShowGutter(false);
            editor.session.setMode('ace/mode/json');
            editor.setValue(JSON.stringify(page.sections || [], null, 2), 0);
            editor.focus();
        });

        this.ui_add(editor_element);
    }
}

export class AdminTranslateScreen extends UIScreen {
    constructor(private _page_id: YId) {
        super();
    }

    protected override async fillContent(content: UIViewSpec) {
        content.classList.add('tl-tabs');

        const page = await f_pages.get(this._page_id);
        $log('app', 'Translating page', page);
        this.title = page.name;

        content.ui_add(new PageTranslationTab(page));
    }
}

export class AdminPageScreen extends UIScreen {
    constructor(private _page_id?: YId) {
        super();
        this.title = _page_id ? 'Page' : 'New page';
    }

    protected override async fillContent(content: UIViewSpec) {
        content.classList.add('tl-tabs');

        const page = this._page_id && await f_pages.get(this._page_id);
        if(page)
            this.title = page.name;

        content.ui_add(new UITabs([
            { title: 'Info', view: () => new PageInfoTab(page) },
            { title: 'Content', view: () => new PageContentTab(page!) },
            { title: 'JSON', view: () => new PageJSONTab(page!) }
        ]));
    }
}

class DuplicateDialog extends UIDialog {
    constructor(page: FPageSectionView) {
        super({ classes: 'm-600 m-mobile-fs' });

        const name = new UITextInput({ required: true });

        const buttons = $el.DIV('l-button-row m-right', [
            new UIButton({
                title: $i18n.tl.b_cancel, classes: 'm-hollow', on_click: () => {
                    this.dismiss(null);
                }
            }),
            new UIButton({
                title: "Duplicate", classes: 'ui-tint-reverse', on_click: () => {
                    if(!$form.validate(name)) return;

                    const new_page = yCopy(page);
                    new_page.name = name.value;
                    if(!canManageLockedPages()) {
                        new_page.is_locked = false;
                        new_page.is_hidden = false;
                    }
                    new_page.resource_id = '';
                    f_pages.insert(new_page).then(() => {
                        this.dismiss(true);
                    }, uiToast);
                }
            })
        ]);

        this.content.ui_add($el.DIV('w-block l-form tl-producer-form m-compact', [
            $el.H2('title', 'Duplicate page'),
            $el.P('text', page.name),
            new UILabeled(name, { above: 'New name' }),
            buttons
        ]));
    }
}

export class AdminPagesScreen extends UIScreen {
    protected override async fillContent(content: UIViewSpec) {
        const [all_pages, all_routes] = await Promise.all([fPagesGetListView(true), f_routes.getAll()]);
        const can_edit = canManagePages();

        const doSearch = () => {
            let pages = all_pages;
            if(!xavis_experiments)
                pages = pages.filter(r => !r.is_hidden);
            pages = pages.filter(r => !r.is_deleted);

            count.number = pages.length;
            source.data = pages;
        }

        const count = new ABObjectCount('pages');

        const filter_bar = new ABFilterBar({
            right_content: [count],
            no_header: true
        });

        const def = new UITableDefinition<FPage>();
        def.addValue(p => p.is_locked, $fmt.CHECKICON(UIIcon.LOCK_KEYHOLE), { width: -32 });
        def.addValue(p => p.name, { title: 'Name', width: 200 });
        def.addValue(p => {
            const routes = all_routes.filter(r => r.target_type == 'page' && r.target_page_id == p.id);
            let r = routes.length > 1 ? `${routes[0].path}\u00a0\u00a0\u00a0+${routes.length - 1}` : routes.length == 1 ? routes[0].path : '';
            if(p.resource_id)
                r += '(' + p.resource_id + ')';
            return r;
        }, { title: 'Routes', width: 200 });
        if(xavis_experiments) {
            // def.addValue(p => p.removed, $fmt.CHECKMARK, { title: 'Removed', width: -80 });
            def.addValue(e => $time(e.updated_at), $fmt.DATE('d MMM yyyy'), { id: 'updated_at', title: 'Updated at', width: -120, optional: true });
            def.addValue(p => p.id, { title: 'ID', width: -280 });
        }

        def.addButtons([
            {
                icon: UIIcon.TRASH_CAN, tooltip: 'Delete', action: item => {
                    return showWarning({ text: "Are you sure you want to delete this page?" }).then(() => {
                        return f_pages.remove(item.id).then(doSearch);
                    });
                }
            },
            {
                icon: UIIcon.COPY, tooltip: 'Duplicate', action: item => {
                    f_pages.get(item.id).then(full_item => {
                        new DuplicateDialog(full_item).present().then(() => {
                            $location.reload();
                        });
                    });
                }
            },
            {
                icon: UIIcon.QUOTE_RIGHT, tooltip: 'Translate', action: item => {
                    $location.push('translate_page', { page_id: item.id });
                }, when: can_edit
            },
            {
                icon: UIIcon.PENCIL, tooltip: 'Edit', action: item => {
                    $location.push('page', { page_id: item.id });
                }, when: can_edit
            },
            { icon: UIIcon.EYE, tooltip: 'Preview', link: item => 'https://abundolive.se/preview_page/' + item.id },
        ], { width: -156 });

        def.controller = row => {
            if(row.item.is_hidden)
                row.classList.add('tl-standby-row');
        };

        def.preference_prefix = 'pages';

        const source = new YArrayDataSource<FPage>;

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block m-wide', [
                filter_bar,
                new UITable(def, source),
            ])),
            makeActionButton(UIIcon.PLUS, 'New page', () => { $location.push('page') })
        ]);

        doSearch();
        this.onViewDidReappear = doSearch;
    }
}

function getAllBlocksInBlocks(blocks: FPageBlock[]): FPageBlock[] {
    const ret: FPageBlock[] = [];

    for(const b of blocks) {
        ret.push(b);
        if(b.type == 'container' || b.type == 'new_hero' || b.type == 'bubble' || b.type == 'hsplit')
            ret.push(...getAllBlocksInBlocks(b.blocks ?? []));
    }

    return ret;
}

(window as any).abGetBlockUsageMap = function() {
    return f_pages.all.filter(p => !p.is_deleted).flatMap(p => p.sections.flatMap(s => getAllBlocksInBlocks(s.blocks).map(b => [p, b.type] as const))).reduce((acc, [p, t]) => { acc[t] = acc[t] || []; if(!acc[t].includes(p)) acc[t].push(p); return acc; }, {} as Record<FPageBlockType, FPageSectionView[]>);
}
